import { resolveApiOrigin } from './api-origin.js';
import { useAuthSession } from '../auth/AuthBootstrap.js';
import { getActiveScopePath } from '../auth/active-scope.js';

/**
 * Header bag carrying the active hierarchy scope (the portal's selected
 * org node) so the server can restrict reads to that subtree. Omitted
 * when no scope is active (admin / non-embed view).
 */
function scopeHeader(): Record<string, string> {
  const scope = getActiveScopePath();
  return scope ? { 'x-canopy-scope': scope } : {};
}

/**
 * API base URL.
 *
 * In dev (Vite proxy on :4000) and in same-origin deploys, the SPA
 * makes relative `/reporting/api/...` calls. In dedicated-subdomain
 * deploys (sage.gocanopy.io ↔ api.sage.gocanopy.io) the container sets
 * `SAGE_API_BASE_URL=https://api.sage.gocanopy.io` at start, which
 * apps/web/docker-entrypoint.sh writes into /runtime-config.js and
 * src/runtime-config.ts reads (CSAGE-437), and the SPA emits absolute URLs.
 *
 * The trailing slash is stripped so we can append `/reporting/api`
 * uniformly. Empty / unset → relative path (preserves existing dev
 * behaviour and the same-origin case).
 */
const API_ORIGIN = resolveApiOrigin();
const API_BASE = `${API_ORIGIN}/reporting/api`;

/**
 * A single attempt: one `fetch` with the given credential.
 *
 * `scopeHeader()` is read here rather than by the caller so a retry re-reads
 * the CURRENT active scope — if the portal changed the selected org between
 * the attempt and the retry, the replay must not carry the stale scope.
 */
async function sendOnce(path: string, token: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    // credentials:'include' lets the per-session `sage-active-tenant`
    // cookie ride along on every API call. The cookie is set by
    // POST /me/tenant when the user switches tenants; the server's
    // tenant middleware reads it and overrides the JWT's tenantId
    // for the request. Without this, the cookie wouldn't be sent
    // on the cross-origin (sage.gocanopy.io ↔ api.sage.gocanopy.io)
    // XHR and the switch wouldn't stick.
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...scopeHeader(),
      ...(init?.headers ?? {}),
    },
  });
}

/**
 * CSAGE-387: send, and on a 401 refresh the credential and replay ONCE.
 *
 * The bug this closes: the SPA held whatever token it bootstrapped with and had
 * no 401 handling anywhere, so once that token passed its TTL (15 min for
 * Sage-OAuth) every call failed with `auth.invalid_token` until the user
 * reloaded. Reads hid it — react-query served them from cache — so it surfaced
 * as writes failing for no visible reason, most often on the slowest form in
 * the app (a report subscription).
 *
 * Exactly one retry, and only on 401. Rules:
 *   * no `onExpired` (callers holding a bare token, e.g. TenantSwitcher) —
 *     behaves exactly as before;
 *   * the refresh REJECTS (not embedded, portal never answers its 5s window) —
 *     surface the ORIGINAL 401 rather than a confusing timeout error. The
 *     caller's `catch` sees the same platform error shape it always did;
 *   * the refresh resolves to the same token (sources with no real refresh,
 *     i.e. urlFragment / devToken) — don't replay, the answer would be
 *     identical. That also makes the "one retry" bound structural rather than
 *     something a future edit could turn into a loop;
 *   * the replay 401s too — return it, no third attempt.
 *
 * `init` is replayed as-is. Safe because every body in this app is a
 * `JSON.stringify` string; a stream body would be consumed by the first
 * attempt and must not be routed through here.
 */
async function sendWithRefresh(
  path: string,
  token: string,
  init: RequestInit | undefined,
  onExpired: (() => Promise<string>) | undefined,
): Promise<Response> {
  const res = await sendOnce(path, token, init);
  if (res.status !== 401 || !onExpired) return res;

  let refreshed: string;
  try {
    refreshed = await onExpired();
  } catch {
    return res;
  }
  if (!refreshed || refreshed === token) return res;
  return sendOnce(path, refreshed, init);
}

/**
 * Typed fetch wrapper. Attaches the Bearer token and surfaces the
 * platform error shape from @canopy/shared unchanged.
 *
 * `onExpired` (CSAGE-387) supplies a fresh credential when the server rejects
 * this one — in practice `AuthSession.refresh`. Optional: omitting it keeps the
 * pre-CSAGE-387 single-attempt behaviour.
 */
export async function apiFetch<T>(
  path: string,
  token: string,
  init?: RequestInit,
  onExpired?: () => Promise<string>,
): Promise<T> {
  const res = await sendWithRefresh(path, token, init, onExpired);
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = { code: 'http.' + res.status, message: res.statusText };
    }
    const err = Object.assign(new Error((body as { message?: string }).message ?? res.statusText), {
      status: res.status,
      body,
    });
    throw err;
  }
  return (await res.json()) as T;
}

/**
 * Raw fetch — returns the Response object, no JSON parsing. Used for
 * binary endpoints (render output) where we want Blob/headers access.
 */
export async function apiFetchRaw(
  path: string,
  token: string,
  init?: RequestInit,
  onExpired?: () => Promise<string>,
): Promise<Response> {
  // Shares sendWithRefresh with apiFetch: a 401 on a render/download is the
  // same expired-credential story, and `credentials: 'include'` (the
  // `sage-active-tenant` cookie, required on every call for tenant-override
  // sessions) is applied there for both paths.
  return sendWithRefresh(path, token, init, onExpired);
}

export function useApi(): {
  fetch: <T>(path: string, init?: RequestInit) => Promise<T>;
  fetchRaw: (path: string, init?: RequestInit) => Promise<Response>;
} {
  const session = useAuthSession();
  // CSAGE-387: every hook-based caller gets refresh-and-retry for free by
  // handing the session's own refresh down as `onExpired`. A successful
  // refresh also updates the session (see AuthBootstrap's `adoptToken`), so
  // the next render's `session.token` is already the live one.
  return {
    fetch: <T>(path: string, init?: RequestInit) =>
      apiFetch<T>(path, session.token, init, session.refresh),
    fetchRaw: (path: string, init?: RequestInit) =>
      apiFetchRaw(path, session.token, init, session.refresh),
  };
}
